import { useState, useEffect } from 'react'
import { Link, Navigate } from 'react-router-dom'
import {
  User, Mail, Lock, Eye, EyeOff, ChevronRight, Save, Check,
  Phone, Shield, ShieldCheck, ShieldOff, Bell, AlertCircle, Pencil, QrCode
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { customerService } from '../services/api'
import api from '../services/api'
import { PageSpinner } from '../components/ui'
import toast from 'react-hot-toast'

export default function Settings() {
  const { user, isAuthenticated, loading } = useAuth()
  if (loading) return <PageSpinner />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/account" className="text-gray-400 hover:text-primary transition-colors">
          <ChevronRight size={18} className="rotate-180" />
        </Link>
        <h1 className="section-title">Profile & Settings</h1>
      </div>
      <div className="space-y-5">
        <ProfileCard user={user} />
        <PasswordCard />
        <TwoFactorCard />
        <NotificationsCard />
      </div>
    </div>
  )
}

// ── Profile info card ──────────────────────────────────────────────
function ProfileCard({ user }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [saved, setSaved] = useState(false)
  const [form, setForm] = useState({ phone: '', display_name: '', email: '' })

  const { data: profile } = useQuery({
    queryKey: ['customer-me'],
    queryFn: () => api.get('/customer/me').then(r => r.data),
    onSuccess: (data) => {
      // Pre-fill form with profile data when it loads
      setForm(f => ({ ...f, phone: data?.phone || '', display_name: data?.name || user?.username || '', email: user?.email || '' }))
    },
  })

  const openEdit = () => {
    setForm({ phone: profile?.phone || '', display_name: profile?.name || user?.username || '', email: user?.email || '' })
    setEditing(true)
  }

  const mut = useMutation({
    mutationFn: (data) => api.put('/customer/me', data),
    onSuccess: () => {
      qc.invalidateQueries(['customer-me'])
      setEditing(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      toast.success('Profile updated')
    },
    onError: () => toast.error('Failed to update profile'),
  })

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <User size={16} className="text-primary" /> Account Info
        </h2>
        {!editing ? (
          <button onClick={openEdit} className="text-xs text-primary hover:underline font-display">
            Edit
          </button>
        ) : (
          <button onClick={() => setEditing(false)} className="text-xs text-gray-400 hover:text-gray-600 font-display">
            Cancel
          </button>
        )}
      </div>

      {/* Avatar */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-2xl font-heading text-primary">
          {user?.username?.[0]?.toUpperCase()}
        </div>
        <div>
          <p className="font-display font-semibold text-gray-900 dark:text-white">{user?.username}</p>
          <p className="text-sm text-gray-400">{user?.email}</p>
          {profile?.price_list_tier && (
            <span className="inline-block text-xs bg-primary/10 text-primary dark:text-primary-light px-2 py-0.5 rounded-full font-display font-medium mt-1">
              Price Tier {profile.price_list_tier}
            </span>
          )}
        </div>
      </div>

      {/* Read-only fields always shown */}
      <div className="space-y-3">
        <ReadField icon={<User size={14} />} label="Username" value={user?.username} />
        <ReadField icon={<Mail size={14} />} label="Email" value={user?.email} />
        {profile?.name && <ReadField icon={<User size={14} />} label="Account Name" value={profile.name} />}
        {profile?.address && (
          <ReadField icon={<User size={14} />} label="Account Address" value={profile.address} />
        )}
      </div>

      {/* Edit form — shown when editing */}
      {editing && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-3 animate-fade-in">
          <p className="text-xs font-display font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Editable fields
          </p>
          <div>
            <label className="block text-xs font-display font-medium text-gray-600 dark:text-gray-300 mb-1.5">
              Display Name
            </label>
            <input
              className="input-field text-sm"
              value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              placeholder="Your display name"
            />
          </div>
          <div>
            <label className="block text-xs font-display font-medium text-gray-600 dark:text-gray-300 mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              className="input-field text-sm"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block text-xs font-display font-medium text-gray-600 dark:text-gray-300 mb-1.5">
              Contact Number
            </label>
            <input
              className="input-field text-sm"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="+27 11 123 4567"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => mut.mutate(form)}
              disabled={mut.isPending}
              className="btn-primary flex items-center gap-2 py-2 px-4 text-sm"
            >
              <Save size={14} />
              {mut.isPending ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="btn-outline py-2 px-4 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!editing && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs text-gray-400">
            To update your account name, address or contact details, please contact your account manager.
          </p>
        </div>
      )}
    </div>
  )
}

function ReadField({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
      <span className="text-gray-400">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 font-display">{label}</p>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{value || '—'}</p>
      </div>
    </div>
  )
}

// ── Two-Factor Authentication card ────────────────────────────────
function TwoFactorCard() {
  const [status, setStatus]     = useState(null)   // { totp_enabled, email_2fa_enabled } or null
  const [step, setStep]         = useState('idle')  // idle | choose_method | setup_totp | setup_email | confirm_disable
  const [qrUrl, setQrUrl]       = useState(null)
  const [secret, setSecret]     = useState(null)
  const [code, setCode]         = useState('')
  const [loading, setLoading]   = useState(false)

  const loadStatus = async () => {
    try {
      const r = await api.get('/user/2fa/status')
      setStatus({ totp_enabled: r.totp_enabled ?? r.enabled ?? false, email_2fa_enabled: r.email_2fa_enabled ?? false })
    } catch { setStatus({ totp_enabled: false, email_2fa_enabled: false }) }
  }

  useEffect(() => { loadStatus() }, [])

  const isEnabled    = status?.totp_enabled || status?.email_2fa_enabled
  const activeMethod = status?.totp_enabled ? 'Authenticator App' : status?.email_2fa_enabled ? 'Email OTP' : null

  // ── TOTP setup ──
  const handleSetupTotp = async () => {
    setLoading(true)
    try {
      const r = await api.post('/user/2fa/setup')
      setQrUrl(r.qr_url); setSecret(r.secret); setCode(''); setStep('setup_totp')
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start 2FA setup')
    } finally { setLoading(false) }
  }

  const handleVerifyTotp = async () => {
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return }
    setLoading(true)
    try {
      await api.post('/user/2fa/verify', { code })
      toast.success('Authenticator 2FA enabled!')
      await loadStatus(); setStep('idle'); setQrUrl(null); setSecret(null); setCode('')
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code — please try again')
    } finally { setLoading(false) }
  }

  // ── Email OTP setup ──
  const handleEnableEmail = async () => {
    setLoading(true)
    try {
      await api.post('/user/email-2fa/enable')
      toast.success('Email 2FA enabled! A code will be sent to your email each time you log in.')
      await loadStatus(); setStep('idle')
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to enable email 2FA')
    } finally { setLoading(false) }
  }

  // ── Disable (any method) ──
  const handleDisable = async () => {
    setLoading(true)
    try {
      if (status?.totp_enabled) await api.delete('/user/2fa')
      if (status?.email_2fa_enabled) await api.post('/user/email-2fa/disable')
      toast.success('2FA disabled')
      await loadStatus(); setStep('idle')
    } catch (err) {
      toast.error('Failed to disable 2FA')
    } finally { setLoading(false) }
  }

  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck size={16} className="text-primary" />
        <h2 className="font-display font-semibold text-gray-900 dark:text-white text-sm">Two-Factor Authentication</h2>
        {isEnabled && (
          <span className="ml-auto text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
            <Check size={10} /> {activeMethod}
          </span>
        )}
        {status !== null && !isEnabled && (
          <span className="ml-auto text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-0.5 rounded-full font-medium">
            Not configured
          </span>
        )}
      </div>

      {/* ── Idle ── */}
      {step === 'idle' && (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
            {isEnabled
              ? `Your account is protected with two-factor authentication (${activeMethod}). Each login requires a verification code.`
              : "Add an extra layer of security. Choose between an authenticator app or a code sent to your email."}
          </p>
          {isEnabled ? (
            <button onClick={() => setStep('confirm_disable')}
              className="flex items-center gap-2 text-sm text-red-500 hover:text-red-400 transition-colors font-medium">
              <ShieldOff size={14} /> Disable 2FA
            </button>
          ) : (
            <button onClick={() => setStep('choose_method')} disabled={loading}
              className="btn-primary text-sm flex items-center gap-2">
              <ShieldCheck size={14} /> Enable 2FA
            </button>
          )}
        </>
      )}

      {/* ── Choose method ── */}
      {step === 'choose_method' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 leading-relaxed">
            Choose how you'd like to receive your verification code each time you sign in:
          </p>
          <button onClick={handleSetupTotp} disabled={loading}
            className="w-full flex items-start gap-3 p-3 rounded-xl border border-surface-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <ShieldCheck size={16} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-display font-semibold text-gray-900 dark:text-white">Authenticator App</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">Use Google Authenticator, Authy, or any TOTP app. Works offline.</p>
            </div>
          </button>
          <button onClick={handleEnableEmail} disabled={loading}
            className="w-full flex items-start gap-3 p-3 rounded-xl border border-surface-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Mail size={16} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-display font-semibold text-gray-900 dark:text-white">Email Code</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">A 6-digit code will be sent to your registered email each time you sign in. Simpler, no app required.</p>
            </div>
          </button>
          <button onClick={() => setStep('idle')} className="w-full text-center text-xs text-gray-400 hover:text-primary transition-colors pt-1">
            Cancel
          </button>
        </div>
      )}

      {/* ── Setup TOTP: QR + code input ── */}
      {step === 'setup_totp' && (
        <div className="space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            Scan this QR code with <strong className="text-gray-700 dark:text-gray-300">Google Authenticator</strong> or any TOTP app, then enter the 6-digit code to confirm.
          </p>
          {qrUrl && (
            <div className="flex justify-center">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrUrl)}&size=180x180&bgcolor=ffffff`} alt="2FA QR Code" className="w-44 h-44 rounded-lg border border-gray-200 dark:border-gray-700" />
            </div>
          )}
          {secret && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2 text-center">
              <p className="text-xs text-gray-400 mb-1">Manual entry key</p>
              <code className="text-xs font-mono text-primary tracking-widest">{secret}</code>
            </div>
          )}
          <div>
            <label className="block text-xs font-display font-medium text-gray-600 dark:text-gray-300 mb-1.5">Verification Code</label>
            <input type="text" inputMode="numeric" maxLength={6} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000" className="input-field text-center text-2xl tracking-[0.4em] font-mono"
              autoComplete="one-time-code" autoFocus />
          </div>
          <div className="flex gap-2">
            <button onClick={handleVerifyTotp} disabled={loading || code.length < 6} className="btn-primary text-sm flex items-center gap-2">
              <Check size={14} /> {loading ? 'Verifying…' : 'Confirm & Enable'}
            </button>
            <button onClick={() => { setStep('choose_method'); setQrUrl(null); setCode('') }}
              className="btn-outline text-sm">Back</button>
          </div>
        </div>
      )}

      {/* ── Confirm disable ── */}
      {step === 'confirm_disable' && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-lg p-3">
            <AlertCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
            <p className="text-xs text-red-600 dark:text-red-400 leading-relaxed">
              Disabling 2FA will make your account less secure. You can re-enable it at any time.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleDisable} disabled={loading}
              className="bg-red-600 hover:bg-red-500 text-white text-sm px-4 py-2 rounded-lg transition-colors flex items-center gap-2">
              <ShieldOff size={14} /> {loading ? 'Disabling…' : 'Yes, disable 2FA'}
            </button>
            <button onClick={() => setStep('idle')} className="btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Password change card ───────────────────────────────────────────
function PasswordCard() {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ current: '', next: '', confirm: '' })
  const [show, setShow] = useState({ current: false, next: false })
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (form.next !== form.confirm) { toast.error('New passwords do not match'); return }
    if (form.next.length < 8) { toast.error('Password must be at least 8 characters'); return }
    setLoading(true)
    try {
      await api.post('/change-password', { current_password: form.current, new_password: form.next })
      toast.success('Password changed successfully')
      setOpen(false)
      setForm({ current: '', next: '', confirm: '' })
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <Shield size={16} className="text-primary" /> Password & Security
        </h2>
        <button onClick={() => setOpen(o => !o)} className="text-xs text-primary hover:underline font-display">
          {open ? 'Cancel' : 'Change'}
        </button>
      </div>

      {!open ? (
        <p className="text-sm text-gray-400 mt-3">••••••••••••</p>
      ) : (
        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <PwField label="Current Password" val={form.current} onChange={v => setForm(f => ({ ...f, current: v }))}
            show={show.current} onToggle={() => setShow(s => ({ ...s, current: !s.current }))} />
          <PwField label="New Password" val={form.next} onChange={v => setForm(f => ({ ...f, next: v }))}
            show={show.next} onToggle={() => setShow(s => ({ ...s, next: !s.next }))} />
          <div>
            <label className="block text-xs font-display font-medium text-gray-600 dark:text-gray-300 mb-1.5">Confirm New Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                className="input-field pl-9 text-sm" placeholder="Repeat new password" />
            </div>
            {form.confirm && form.next && form.confirm !== form.next && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle size={11} /> Passwords don't match</p>
            )}
          </div>
          <button type="submit" disabled={loading || !form.current || !form.next || !form.confirm}
            className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            <Save size={14} /> {loading ? 'Saving…' : 'Update Password'}
          </button>
        </form>
      )}
    </div>
  )
}

function PwField({ label, val, onChange, show, onToggle }) {
  return (
    <div>
      <label className="block text-xs font-display font-medium text-gray-600 dark:text-gray-300 mb-1.5">{label}</label>
      <div className="relative">
        <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type={show ? 'text' : 'password'} value={val} onChange={e => onChange(e.target.value)}
          className="input-field pl-9 pr-10 text-sm" />
        <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  )
}

// ── Notifications prefs card ───────────────────────────────────────
function NotificationsCard() {
  const [prefs, setPrefs] = useState({ order_updates: true, weekly_digest: false })
  const [promoSub, setPromoSub] = useState(null)   // null=loading, true/false
  const [promoSaving, setPromoSaving] = useState(false)
  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }))

  // Load current subscribe status from server
  useState(() => {
    api.get('/customer/subscription-status')
      .then(r => setPromoSub(r.data.promotional_emails ?? true))
      .catch(() => setPromoSub(true))
  })

  const handlePromoToggle = async () => {
    const next = !promoSub
    setPromoSaving(true)
    try {
      await api.post(next ? '/customer/subscribe' : '/customer/unsubscribe')
      setPromoSub(next)
      toast.success(next ? 'Subscribed to promotional emails' : 'Unsubscribed from promotional emails')
    } catch {
      toast.error('Could not update preference')
    } finally {
      setPromoSaving(false)
    }
  }

  const localPrefs = [
    { key: 'order_updates', label: 'Order confirmations & updates', desc: 'Email when your order status changes' },
    { key: 'weekly_digest', label: 'Weekly order summary', desc: 'Your weekly purchase digest' },
  ]

  return (
    <div className="card p-6">
      <h2 className="font-display font-semibold text-gray-800 dark:text-white flex items-center gap-2 mb-4">
        <Bell size={16} className="text-primary" /> Notification Preferences
      </h2>
      <div className="space-y-3">
        {/* Promotional emails — server-backed */}
        <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
          <div>
            <p className="text-sm font-display font-medium text-gray-700 dark:text-gray-200">Promotions &amp; exclusive deals</p>
            <p className="text-xs text-gray-400 mt-0.5">Deals, specials and new product alerts via email</p>
          </div>
          <button
            onClick={handlePromoToggle}
            disabled={promoSaving || promoSub === null}
            className={`relative inline-flex w-11 h-6 rounded-full transition-colors flex-shrink-0 ${promoSub ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-600'} disabled:opacity-60`}
          >
            <span className={`inline-block w-5 h-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${promoSub ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Local prefs */}
        {localPrefs.map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div>
              <p className="text-sm font-display font-medium text-gray-700 dark:text-gray-200">{label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
            </div>
            <button onClick={() => toggle(key)}
              className={`relative inline-flex w-11 h-6 rounded-full transition-colors flex-shrink-0 ${prefs[key] ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-600'}`}>
              <span className={`inline-block w-5 h-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${prefs[key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-3">Promotional email preference is saved to your account. Other preferences are stored locally.</p>
    </div>
  )
}
